import { formatDate } from 'helpers/formatDate';
import { getTranslation } from 'helpers/getTranslation';

import {
  formatDaysInactive,
  formatProgressPercent,
  getRiskBadgeClassName,
  getRiskLevelLabel,
  getRiskNotApplicableBadgeClassName,
  getRiskNotApplicableLabel,
  getStatusBadgeClassName,
  getStudentDisplayName as getStudentDisplayNameFromName,
  getStudentStatusLabel,
  hasStudentName,
  isRiskApplicable,
  type CuratorReportApiRiskLevel,
  type CuratorReportStudentStatus,
} from '../SchoolCuratorReportPage/SchoolCuratorReportPage.helpers';

export type CuratorStudentDetailLesson = {
  id: string;
  title: string;
  type: string;
  completed: boolean;
};

export type CuratorStudentDetailModule = {
  id: string;
  title: string;
  preview: string | null;
  completed: boolean;
  progress: number;
  lessons: CuratorStudentDetailLesson[];
};

export type CuratorStudentDetailChartSlice = {
  id: string;
  label: string;
  value: number;
  color: string;
};

export const OVERVIEW_COURSES_PROGRESS_COLORS = [
  '#1182f6',
  '#8b5cf6',
  '#06b6d4',
  '#f59e0b',
  '#ec4899',
];

export type CuratorStudentDetailOverviewCourse = {
  id: string;
  title: string;
  progress: number;
  status: CuratorReportStudentStatus;
  isCurrent: boolean;
  completedLessonsCount: number;
  totalLessonsCount: number;
  learnTimeSeconds: number;
};

export type CuratorStudentDetailOverview = {
  courses: CuratorStudentDetailOverviewCourse[];
  charts: {
    byStatus: CuratorStudentDetailChartSlice[];
    byProgress: CuratorStudentDetailChartSlice[];
  };
};

export type CuratorStudentDetailCourseBreakdown = {
  id: string;
  title: string;
  structure: 'MODULES' | 'LESSONS';
  preview: string | null;
  progress: number;
  modules: CuratorStudentDetailModule[] | null;
  lessons: CuratorStudentDetailLesson[] | null;
};

export type CuratorStudentDetailPromocodeAccess = {
  code: string | null;
  startDate: string | null;
  endDate: string | null;
} | null;

export type CuratorStudentDetailData = {
  student: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    avatar: string | null;
    registeredAt: string | null;
    promocodeAccess: CuratorStudentDetailPromocodeAccess;
  };
  course: {
    id: string;
    title: string;
    structure: 'MODULES' | 'LESSONS';
  };
  summary: {
    progress: number;
    status: CuratorReportStudentStatus;
    currentLesson: { id: string; title: string } | null;
    daysInactive: number;
    lessonsLast7d: number;
    startedAt: string | null;
    completedAt: string | null;
    completedLessonsCount: number;
    totalLessonsCount: number;
    pace: {
      lessonsPerWeek: number;
      cohortMedian: number;
    };
    risk: {
      level: CuratorReportApiRiskLevel;
      score: number;
      reasons: string[];
    };
  };
  modules: CuratorStudentDetailModule[] | null;
  lessons: CuratorStudentDetailLesson[] | null;
  overview: CuratorStudentDetailOverview;
  courseBreakdowns: CuratorStudentDetailCourseBreakdown[];
};

type ApiCuratorStudentDetailResponse = CuratorStudentDetailData;

const getStudentFullName = (student: CuratorStudentDetailData['student']) => {
  const fullName = [student.firstName, student.lastName]
    .filter(Boolean)
    .join(' ');

  return fullName || null;
};

export const hasStudentDetailName = (
  student: CuratorStudentDetailData['student'],
) => hasStudentName(getStudentFullName(student));

export const getStudentDisplayName = (
  student: CuratorStudentDetailData['student'],
) => getStudentDisplayNameFromName(getStudentFullName(student));

export const getStudentAvatarText = (
  student: CuratorStudentDetailData['student'],
) => getStudentFullName(student) || student.email || '';

const formatPromocodeDate = (
  date: string | null,
  locale: string,
): string | null => {
  if (!date) {
    return null;
  }

  if (date === 'NEVER') {
    return getTranslation('common.text.never');
  }

  return formatDate(date, 'dd MMM yyyy', locale);
};

export const formatStudentRegisteredAt = (
  registeredAt: string | null,
  locale: string,
) => {
  if (!registeredAt) {
    return getTranslation('school_curator_student_page.header.not_available');
  }

  return formatDate(registeredAt, 'dd MMM yyyy', locale);
};

export const formatPromocodeCode = (
  promocodeAccess: CuratorStudentDetailPromocodeAccess,
) => {
  if (!promocodeAccess?.code) {
    return getTranslation('school_curator_student_page.header.not_available');
  }

  return promocodeAccess.code;
};

export const formatPromocodeAccessRange = (
  promocodeAccess: CuratorStudentDetailPromocodeAccess,
  locale: string,
) => {
  if (!promocodeAccess) {
    return getTranslation('school_curator_student_page.header.not_available');
  }

  const notAvailable = getTranslation(
    'school_curator_student_page.header.not_available',
  );
  const from = formatPromocodeDate(promocodeAccess.startDate, locale);
  const to = formatPromocodeDate(promocodeAccess.endDate, locale);

  if (from && to) {
    return getTranslation(
      'school_curator_student_page.header.promocode_range',
      { from, to },
    );
  }

  if (from) {
    return getTranslation(
      'school_curator_student_page.header.promocode_range',
      { from, to: notAvailable },
    );
  }

  if (to) {
    return getTranslation(
      'school_curator_student_page.header.promocode_range',
      { from: notAvailable, to },
    );
  }

  return notAvailable;
};

export const formatLearnTimeSeconds = (
  seconds: number,
  locale = 'ru',
): string => {
  if (!seconds || seconds <= 0) {
    return locale.startsWith('en') ? '0 min' : '0 мин';
  }

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const isEn = locale.startsWith('en');

  if (hours === 0) {
    return isEn ? `${minutes} min` : `${minutes} мин`;
  }

  if (minutes === 0) {
    return isEn ? `${hours} h` : `${hours} ч`;
  }

  return isEn ? `${hours} h ${minutes} min` : `${hours} ч ${minutes} мин`;
};

export const mapCuratorStudentDetailResponse = (
  response: ApiCuratorStudentDetailResponse,
): CuratorStudentDetailData => ({
  student: {
    id: response.student.id,
    email: response.student.email ?? '',
    firstName: response.student.firstName ?? null,
    lastName: response.student.lastName ?? null,
    avatar: response.student.avatar ?? null,
    registeredAt: response.student.registeredAt ?? null,
    promocodeAccess: response.student.promocodeAccess
      ? {
          code: response.student.promocodeAccess.code ?? null,
          startDate: response.student.promocodeAccess.startDate ?? null,
          endDate: response.student.promocodeAccess.endDate ?? null,
        }
      : null,
  },
  course: response.course,
  summary: {
    progress: response.summary.progress ?? 0,
    status: response.summary.status,
    currentLesson: response.summary.currentLesson ?? null,
    daysInactive: response.summary.daysInactive ?? 0,
    lessonsLast7d: response.summary.lessonsLast7d ?? 0,
    startedAt: response.summary.startedAt ?? null,
    completedAt: response.summary.completedAt ?? null,
    completedLessonsCount: response.summary.completedLessonsCount ?? 0,
    totalLessonsCount: response.summary.totalLessonsCount ?? 0,
    pace: {
      lessonsPerWeek: response.summary.pace?.lessonsPerWeek ?? 0,
      cohortMedian: response.summary.pace?.cohortMedian ?? 0,
    },
    risk: {
      level: response.summary.risk?.level ?? 'ok',
      score: response.summary.risk?.score ?? 0,
      reasons: response.summary.risk?.reasons ?? [],
    },
  },
  modules: response.modules ?? null,
  lessons: response.lessons ?? null,
  courseBreakdowns: (response.courseBreakdowns ?? []).map((breakdown) => ({
    id: breakdown.id,
    title: breakdown.title ?? '',
    structure: breakdown.structure,
    preview: breakdown.preview ?? null,
    progress: breakdown.progress ?? 0,
    modules: breakdown.modules ?? null,
    lessons: breakdown.lessons ?? null,
  })),
  overview: {
    courses: (response.overview?.courses ?? []).map((course) => ({
      id: course.id,
      title: course.title ?? '',
      progress: course.progress ?? 0,
      status: course.status,
      isCurrent: Boolean(course.isCurrent),
      completedLessonsCount: course.completedLessonsCount ?? 0,
      totalLessonsCount: course.totalLessonsCount ?? 0,
      learnTimeSeconds: course.learnTimeSeconds ?? 0,
    })),
    charts: {
      byStatus: (response.overview?.charts?.byStatus ?? []).map((slice) => ({
        id: slice.id,
        label: slice.label,
        value: slice.value,
        color: slice.color,
      })),
      byProgress: (response.overview?.charts?.byProgress ?? []).map(
        (slice) => ({
          id: slice.id,
          label: slice.label,
          value: slice.value,
          color: slice.color,
        }),
      ),
    },
  },
});

export const getActiveCourseBreakdown = (
  breakdowns: CuratorStudentDetailCourseBreakdown[],
  activeCourseId: string,
) => breakdowns.find((breakdown) => breakdown.id === activeCourseId) ?? null;

export const getCurrentLessonIdForBreakdown = (
  breakdown: CuratorStudentDetailCourseBreakdown,
  selectedCourseId: string,
  selectedCurrentLessonId: string | null | undefined,
): string | null => {
  if (breakdown.id === selectedCourseId && selectedCurrentLessonId) {
    return selectedCurrentLessonId;
  }

  const lessons =
    breakdown.structure === 'MODULES'
      ? (breakdown.modules ?? []).flatMap((module) => module.lessons)
      : breakdown.lessons ?? [];

  return lessons.find((lesson) => !lesson.completed)?.id ?? null;
};

export const getCourseTabLabel = (title: string) =>
  title.replace(/^Тренажер\.\s*/i, '').trim() || title;

export const getCourseProgressDonutData = (
  progress: number,
  color: string,
): CuratorStudentDetailChartSlice[] => {
  const clamped = Math.min(100, Math.max(0, progress));
  const rounded = Math.round(clamped);

  if (rounded >= 100) {
    return [{ id: 'completed', label: 'completed', value: 100, color }];
  }

  if (rounded <= 0) {
    return [{ id: 'remaining', label: 'remaining', value: 100, color: '#eef3fa' }];
  }

  return [
    { id: 'completed', label: 'completed', value: clamped, color },
    {
      id: 'remaining',
      label: 'remaining',
      value: 100 - clamped,
      color: '#eef3fa',
    },
  ];
};

export const getCourseProgressDonutPadAngle = (progress: number) =>
  getCourseProgressDonutData(progress, '#000').length > 1 ? 0.7 : 0;

export {
  formatDaysInactive,
  formatProgressPercent,
  getRiskBadgeClassName,
  getRiskLevelLabel,
  getRiskNotApplicableBadgeClassName,
  getRiskNotApplicableLabel,
  getStatusBadgeClassName,
  getStudentStatusLabel,
  isRiskApplicable,
};

export const getMetricCards = (data: CuratorStudentDetailData) => [
  {
    key: 'status',
    label: getTranslation('school_curator_student_page.metrics.status'),
    type: 'status' as const,
    value: data.summary.status,
  },
  {
    key: 'risk',
    label: getTranslation('school_curator_student_page.metrics.risk'),
    type: 'risk' as const,
    value: data.summary.risk,
  },
  {
    key: 'daysInactive',
    label: getTranslation('school_curator_student_page.metrics.days_inactive'),
    type: 'text' as const,
    value: formatDaysInactive(data.summary.daysInactive),
  },
  {
    key: 'lessonsLast7d',
    label: getTranslation('school_curator_student_page.metrics.lessons_last_7d'),
    type: 'text' as const,
    value: String(data.summary.lessonsLast7d),
  },
];
